import { keyframes, useTheme } from '@mui/material';

import { CAST_PILOT_ASSISTANT } from '@cast/constants';
import {
  Badge,
  Box,
  Button,
  Icons,
  tokens,
  Tooltip,
} from '@cast/design-system';

import { useOrganizations } from '@cast/shared-organization-context-data-access';

import {
  useChatbotState,
  useUnreadCount,
} from '../components/ai-assistant/useChatbotState';

const rotate = keyframes`
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to   { transform: translate(-50%, -50%) rotate(360deg); }
`;

type AskCopilotButtonProps = {
  onClick: () => void;
  promptToEnable?: boolean;
  disabled?: boolean;
  tooltip?: string;
};

export const AskCopilotButton = ({
  onClick,
  promptToEnable = false,
  disabled = false,
  tooltip,
}: AskCopilotButtonProps) => {
  const theme = useTheme();
  const { isProcessing } = useChatbotState();
  const { currentOrganization } = useOrganizations();
  const unreadCount = useUnreadCount(currentOrganization?.id);

  const showGlow = isProcessing || promptToEnable;

  return (
    <Box
      data-testid={
        promptToEnable ? 'try-castpilot-assistant-button' : 'ask-copilot-button'
      }
      sx={{ position: 'relative', display: 'inline-flex' }}
    >
      <Box
        sx={{
          position: 'relative',
          borderRadius: '6px',
          outline: `1px solid ${tokens.theme.border.structural.neutral}`,
          outlineOffset: '-1px',
          overflow: 'hidden',
          ...(showGlow && {
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: '400px',
              height: '400px',
              borderRadius: '50%',
              background:
                'conic-gradient(from 0deg, transparent 0%, transparent 50%, #93C5FD 65%, #2563EB 75%, #93C5FD 85%, transparent 100%)',
              animation: `${rotate} 2s linear infinite`,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: '1px',
              borderRadius: '5px',
              backgroundColor: tokens.theme.bg.structural.surface,
            },
          }),
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Tooltip title={tooltip} placement="bottom">
            <span>
              <Button
                variant="ghost"
                size="sm"
                className="TopNavHeaderButton-root"
                onClick={onClick}
                disabled={disabled}
                startIcon={
                  <Icons.CastCopilot
                    size={16}
                    color={tokens.theme.fg.action.primary}
                  />
                }
                sx={{ border: 'none', ...theme.typography.P14M }}
                data-testid={
                  promptToEnable
                    ? 'try-castpilot-assistant-toggle'
                    : 'ai-assistant-toggle'
                }
              >
                {promptToEnable
                  ? `Try ${CAST_PILOT_ASSISTANT}`
                  : CAST_PILOT_ASSISTANT}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {!promptToEnable && unreadCount > 0 && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            pointerEvents: 'none',
            '& .DsBadge-wrapper': { width: '100%', height: '100%' },
          }}
        >
          <Badge
            count={unreadCount}
            displayIf
            horizontal="right"
            vertical="top"
            size="small"
            variant="primary"
          >
            <Box />
          </Badge>
        </Box>
      )}
    </Box>
  );
};
